import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { AlertTriangle, Plus, Eye, EyeOff, Clock, MapPin, UserX, Shield, Wrench, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const AVERTISSEMENT_ICONS = {
  "retard": Clock,
  "geo_hors_zone": MapPin,
  "absence_non_justifiee": UserX,
  "equipement": Wrench,
  "securite": Shield,
  "autre": Info
};

export const EmployeeAvertissements = ({ employeeId, employeeNom, canManage = false }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [avertissements, setAvertissements] = useState([]);
  const [saison, setSaison] = useState("");
  
  // Modal nouvel avertissement
  const [showNewModal, setShowNewModal] = useState(false);
  const [newType, setNewType] = useState("autre");
  const [newDescription, setNewDescription] = useState("");
  const [newSeverite, setNewSeverite] = useState("mineur");
  const [newDateIncident, setNewDateIncident] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    // Déterminer saison actuelle
    const now = new Date();
    const mois = now.getMonth() + 1;
    const annee = now.getFullYear();
    
    let saisonActuelle;
    if (mois >= 5 && mois <= 10) {
      saisonActuelle = `Été ${annee}`;
    } else if (mois >= 11) {
      saisonActuelle = `Hiver ${annee}-${annee+1}`;
    } else {
      saisonActuelle = `Hiver ${annee-1}-${annee}`;
    }
    
    setSaison(saisonActuelle);
  }, []);

  useEffect(() => {
    if (employeeId && saison) {
      loadData();
    }
  }, [employeeId, saison]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${api.baseUrl}/avertissements/stats/${employeeId}?saison=${encodeURIComponent(saison)}`),
        axios.get(`${api.baseUrl}/avertissements/employee/${employeeId}?saison=${encodeURIComponent(saison)}`)
      ]);

      const statsData = statsRes.data;
      const listData = listRes.data;

      setStats(statsData);
      setAvertissements(listData.avertissements || []);
    } catch (error) {
      console.error("Erreur chargement avertissements:", error);
    } finally {
      setLoading(false);
    }
  };

  const createAvertissement = async () => {
    if (!newDescription.trim()) {
      toast.error("La description est requise");
      return;
    }

    try {
      // 👥 E4b — même route que /gestion-avertissements (source unique, permission `avertissements_modifier`)
      const response = await axios.post(`${api.baseUrl}/avertissements-auto/creer-manuel`, {
          employee_id: employeeId,
          type: newType,
          description: newDescription,
          date_incident: newDateIncident,
          severite: newSeverite,
          emis_par: "Manager"
        });

      if (response.status === 200) {
        toast.success("✅ Avertissement créé et notifié");
        setShowNewModal(false);
        setNewDescription("");
        setNewType("autre");
        loadData();
      } else {
        toast.error("Erreur lors de la création");
      }
    } catch (error) {
      console.error("Erreur création:", error);
      toast.error("Erreur lors de la création");
    }
  };

  const markSeen = async (avertissementId) => {
    try {
      const response = await axios.put(`${api.baseUrl}/avertissements/${avertissementId}/mark-seen`, null);

      if (response.status === 200) {
        toast.success("Marqué comme vu");
        loadData();
      }
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Chargement...</div>;
  }

  const total = stats?.total || 0;
  const flagMesure = stats?.flag_mesure_disciplinaire || false;

  return (
    <div className="space-y-4">
      {/* Header avec indicateur */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className={`w-6 h-6 ${flagMesure ? 'text-red-600' : 'text-amber-600'}`} />
          <div>
            <h3 className="font-semibold text-lg">Avertissements</h3>
            <p className="text-sm text-slate-500">{saison}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Indicateur 3/3 */}
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((num) => (
              <div
                key={num}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  total >= num 
                    ? 'bg-red-500 text-white' 
                    : 'bg-slate-200 text-slate-400'
                }`}
              >
                {num}
              </div>
            ))}
          </div>

          {flagMesure && (
            <Badge variant="destructive" className="animate-pulse">
              MESURE DISCIPLINAIRE
            </Badge>
          )}

          {/* 🆕 [Phase 69 — 2026-06-18] Fiche employé = LECTURE SEULE.
              La création d'avertissements manuels passe désormais
              EXCLUSIVEMENT par la page Gestion des Avertissements
              (source unique). Lien d'accès rapide ci-dessous. */}
          {canManage && (
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setShowNewModal(true)} data-testid="btn-nouvel-avertissement">
              <Plus className="w-4 h-4 mr-1" /> Nouvel avertissement
            </Button>
          )}
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="link-gestion-avertissements"
          >
            <a href="/gestion-avertissements" rel="noopener">
              <AlertTriangle className="w-4 h-4 mr-1" />
              Gérer les avertissements
            </a>
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Automatiques</p>
              <p className="text-2xl font-bold text-blue-600">
                {Object.values(stats.par_type || {}).reduce((sum, count) => 
                  sum + (avertissements.filter(a => a.type === Object.keys(stats.par_type).find(k => stats.par_type[k] === count) && a.categorie === 'automatique').length || 0), 0
                )}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Non vus</p>
              <p className="text-2xl font-bold text-amber-600">{stats.non_vus || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Liste des avertissements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historique</CardTitle>
        </CardHeader>
        <CardContent>
          {avertissements.length === 0 ? (
            <p className="text-center text-slate-500 py-6">Aucun avertissement cette saison</p>
          ) : (
            <div className="space-y-3">
              {avertissements.map((av) => {
                const Icon = AVERTISSEMENT_ICONS[av.type] || Info;
                
                return (
                  <div 
                    key={av.id} 
                    className={`p-4 border rounded-lg ${av.vu_par_employe ? 'bg-slate-50' : 'bg-amber-50 border-amber-300'}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${av.categorie === 'automatique' ? 'text-blue-600' : 'text-amber-600'}`} />
                        <span className="font-medium text-sm capitalize">{av.type.replace('_', ' ')}</span>
                        <Badge variant="outline" className="text-xs">
                          {av.categorie}
                        </Badge>
                        {av.severite === "grave" && (
                          <Badge variant="destructive" className="text-xs">Grave</Badge>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">{new Date(av.date_incident).toLocaleDateString('fr-CA')}</span>
                    </div>

                    <p className="text-sm text-slate-700 mb-2">{av.description}</p>

                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Émis par: {av.emis_par}</span>
                      {!av.vu_par_employe ? (
                        <Button 
                          onClick={() => markSeen(av.id)}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Marquer vu
                        </Button>
                      ) : (
                        <span className="flex items-center gap-1 text-green-600">
                          <EyeOff className="w-3 h-3" />
                          Vu le {new Date(av.date_vu).toLocaleDateString('fr-CA')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Nouvel Avertissement */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvel Avertissement Manuel</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equipement">Mauvais équipement</SelectItem>
                  <SelectItem value="securite">Sécurité non respectée</SelectItem>
                  <SelectItem value="comportement">Comportement</SelectItem>
                  <SelectItem value="qualite">Qualité du travail</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date de l'incident</Label>
              <Input
                type="date"
                value={newDateIncident}
                onChange={(e) => setNewDateIncident(e.target.value)}
              />
            </div>

            <div>
              <Label>Sévérité</Label>
              <Select value={newSeverite} onValueChange={setNewSeverite}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mineur">Mineur</SelectItem>
                  <SelectItem value="moyen">Moyen</SelectItem>
                  <SelectItem value="grave">Grave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Décrivez l'incident..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Annuler
            </Button>
            <Button onClick={createAvertissement} className="bg-amber-600 hover:bg-amber-700">
              Créer et Notifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
